import { ElementDependencyAndCondition } from "@/types/elements";
import { getHash } from "@/util/hash";
import { TreeNode } from "@/types/tree";
import { renderTags, renderTwig } from "@/lib/twig/twig";
import { configStore } from "@/store/store-accessor";
import NotificationLogger from "@/lib/notifications/notifications";
import {
  addOrUpdateGeneratedElementDependenciesCSSInCanvasHead,
  addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead
} from "@/lib/canvas/assets/css-in-head";
import {
  addUniqueScriptToHead,
  processAndAddInlineScriptsToHead
} from "@/lib/canvas/assets/js-in-head";
import { Tags } from "@/types/tags";
import { getDesignForElementFromPreset } from "@/lib/presets/presets";

export async function addBuilderElementDependenciesToHead(
  canvasDocument: Document,
  {
    dependencies,
    element,
    tags = {},
    addInlineScriptsAsUnique = true
  }: {
    dependencies: ElementDependencyAndCondition[];
    element: TreeNode;
    tags?: Tags;
    addInlineScriptsAsUnique?: boolean;
  }
) {
  const elementProperties = element?.data.properties;
  const propsAndPreset = {
    ...elementProperties,
    design: getDesignForElementFromPreset(element)
  };

  for (const dependency of dependencies) {
    // Add dependencies only if there's no condition, or if it's met.
    if (dependency.builderCondition && element) {
      const processedCondition = renderTwig(dependency.builderCondition, {
        ...propsAndPreset,
        elementId: element.id
      });

      if (!processedCondition) continue;

      try {
        const conditionFunction = new Function(processedCondition);

        if (!conditionFunction()) {
          continue;
        }
      } catch (e) {
        NotificationLogger.log.message(
          `Error when running 'builderCondition' for element '${element.data.type}': ${e}`
        );
      }
    }

    const inlineScriptsWithTags = dependency.inlineScripts?.map(script =>
      renderTags(script, tags)
    );
    const inlineStylesWithTags = dependency.inlineStyles?.map(script =>
      renderTags(script, tags)
    );

    /*
      Run the inlineScripts after all the scripts have loaded.
      We do this because inlineScripts are likely dependant in the 'script' loading first
      If there are no scripts, run the inlineScripts by themselves
   */
    if (dependency.scripts?.length) {
      const srcScriptPromises = dependency.scripts.map(
        async scriptUrl =>
          await addUniqueScriptToHead(canvasDocument, { scriptUrl })
      );

      try {
        await Promise.all(srcScriptPromises);

        processAndAddInlineScriptsToHead(
          canvasDocument,
          inlineScriptsWithTags,
          propsAndPreset,
          addInlineScriptsAsUnique
        );
      } catch {
        console.error(
          `Couldn't load some of the inlineScript dependencies because one of more scripts failed to load:${dependency.scripts}`
        );
        NotificationLogger.log.message(
          "Error loading  some of the dependencies, check your console"
        );
      }
    } else {
      processAndAddInlineScriptsToHead(
        canvasDocument,
        inlineScriptsWithTags,
        propsAndPreset,
        addInlineScriptsAsUnique
      );
    }

    dependency.styles?.forEach(stylesheetUrl => {
      addOrUpdateGeneratedElementDependenciesCSSInCanvasHead(
        canvasDocument,
        getHash(stylesheetUrl),
        stylesheetUrl
      );
    });

    dependency.googleFonts?.forEach(fontFamily => {
      const stylesheetUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
        fontFamily
      )}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'`;
      addOrUpdateGeneratedElementDependenciesCSSInCanvasHead(
        canvasDocument,
        getHash(stylesheetUrl),
        stylesheetUrl
      );
    });

    inlineStylesWithTags?.forEach(inlineStyles => {
      const processedStyles = elementProperties
        ? renderTwig(inlineStyles, elementProperties)
        : inlineStyles;

      if (!processedStyles) return;

      addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead(
        canvasDocument,
        getHash(processedStyles),
        processedStyles
      );
    });
  }
}

export async function addBuilderElementConditionalDependenciesToHead(
  canvasDocument: Document,
  element: TreeNode,
  tags = {}
) {
  if (element?.data.type) {
    const elementDependencies = configStore.element(
      element.data.type
    ).dependencies;
    const dependenciesWithBuilderCondition = [
      ...(elementDependencies || []),
      ...(configStore.elementsExternalAttrsDepsAndActions.dependencies || [])
    ].filter(dependency => dependency.builderCondition);

    if (dependenciesWithBuilderCondition.length) {
      return await addBuilderElementDependenciesToHead(canvasDocument, {
        dependencies: dependenciesWithBuilderCondition,
        element,
        tags
      });
    }
  }

  return false;
}
